import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Wallet, Sparkles, Pencil, Trash2 } from "lucide-react";
import { TeamMemberPayment } from "../../../../Data/teamMemberPayment.dto";
import MarketplaceEmptyState from "../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState";
import {
  MenuContainer,
  ThreeDotsButton,
  Dot,
  DropdownMenu,
  MenuItem,
  ItemIconContainer,
  ItemLabel,
  Backdrop,
} from "../../../organisms/Dashboard/documentSharedStyles";
import type { MenuCoords } from "../../../organisms/Dashboard/quotationMasterListShared";
import { usePermissions } from "../../../../hooks/usePermissions";

interface TeamMemberPaymentListProps {
  payments: TeamMemberPayment[];
  onUpdate?: (paymentId: number | string) => void;
  onDelete?: (paymentId: number | string) => void;
  compact?: boolean;
  embedded?: boolean;
}

const ACTION_BUTTONS = [
  { key: "update", icon: Pencil, label: "Update" },
  { key: "delete", icon: Trash2, label: "Delete" },
] as const;

const TeamMemberPaymentList = ({
  payments,
  onUpdate,
  onDelete,
  compact = false,
  embedded = false,
}: TeamMemberPaymentListProps) => {
  const { hasPermission } = usePermissions();
  const canUpdate = !!onUpdate && hasPermission("payments_management.update");
  const canDelete = !!onDelete && hasPermission("payments_management.delete");
  const hasActions = canUpdate || canDelete;

  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatAmount = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) return String(amount);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(num);
  };

  const capitalize = (value: string) =>
    (value ?? "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: string) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[id];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const estimatedMenuHeight = 120;
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    window.addEventListener("resize", closeMenu);
    return () => window.removeEventListener("resize", closeMenu);
  }, [activeMenuId, closeMenu]);

  const activePayment = useMemo(
    () => payments.find((p) => String(p.id) === activeMenuId) ?? null,
    [payments, activeMenuId]
  );

  const isFullyPaid = (payment: TeamMemberPayment) => {
    const remaining =
      typeof payment.remaining_amount === "string"
        ? parseFloat(payment.remaining_amount)
        : Number(payment.remaining_amount ?? NaN);
    return (
      (payment.status ?? "").toLowerCase() === "completed" &&
      !Number.isNaN(remaining) &&
      remaining === 0
    );
  };
  const fullHeaders = [
    "Member",
    "Total",
    "Paid",
    "Remaining",
    "Method",
    "Date",
    "Type",
    "Status",
    "Reference",
    "Receipt",
    ...(hasActions ? ["Actions"] : []),
  ];

  const compactHeaders = [
    "Total",
    "Paid",
    "Remaining",
    "Date",
    "Type",
    "Status",
    ...(hasActions ? ["Actions"] : []),
  ];

  const headers = compact ? compactHeaders : fullHeaders;

  const renderActionsCell = (payment: TeamMemberPayment) => {
    if (!hasActions) return null;
    const paymentId = String(payment.id);
    return (
      <td style={{ textAlign: "right" }}>
        <MenuContainer>
          <ThreeDotsButton
            ref={(el) => {
              menuButtonRefs.current[paymentId] = el;
            }}
            type="button"
            aria-expanded={activeMenuId === paymentId}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMenu(paymentId);
            }}
          >
            <Dot />
            <Dot />
            <Dot />
          </ThreeDotsButton>
        </MenuContainer>
      </td>
    );
  };

  return (
    <ListPanel $embedded={embedded}>
      {!embedded && (
        <ListHeader>
          <ListTitle>Payment history</ListTitle>
          {payments.length > 0 && (
            <CountBadge>
              {payments.length} result{payments.length !== 1 ? "s" : ""}
            </CountBadge>
          )}
        </ListHeader>
      )}

      {payments.length === 0 ? (
        <MarketplaceEmptyState
          padding="32px 0 24px"
          icon={<Wallet size={36} strokeWidth={1.5} />}
          title="No payments found"
          description="Create a manual payment to record team member payouts and track them here."
          hint={{
            icon: <Sparkles size={13} />,
            text: "Salary, bonus, advance & reimbursement payments appear here",
          }}
        />
      ) : (
        <TableWrap>
          <PaymentsTable $minWidth={compact ? 620 : 1100}>
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h} style={h === "Actions" ? { textAlign: "right" } : undefined}>
                    <span>{h}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) =>
                compact ? (
                  <tr key={payment.id}>
                    <td>
                      <AmountCell>{formatAmount(payment.amount)}</AmountCell>
                    </td>
                    <td>
                      {payment.paid_amount != null ? formatAmount(payment.paid_amount) : "—"}
                    </td>
                    <td>
                      {payment.remaining_amount != null ? (
                        <RemainingCell>{formatAmount(payment.remaining_amount)}</RemainingCell>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      {(payment.payment_type ?? "—").replace(/_/g, " ")}
                    </td>
                    <td>
                      <StatusPill $status={payment.status ?? ""}>
                        {capitalize(payment.status ?? "")}
                      </StatusPill>
                    </td>
                    {renderActionsCell(payment)}
                  </tr>
                ) : (
                  <tr key={payment.id}>
                    <td>
                      <MemberName>
                        {payment.team_member_name ?? `ID: ${payment.team_member_id}`}
                      </MemberName>
                    </td>
                    <td>
                      <AmountCell>{formatAmount(payment.amount)}</AmountCell>
                    </td>
                    <td>
                      {payment.paid_amount != null ? formatAmount(payment.paid_amount) : "—"}
                    </td>
                    <td>
                      {payment.remaining_amount != null ? (
                        <RemainingCell>{formatAmount(payment.remaining_amount)}</RemainingCell>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ textTransform: "capitalize" }}>
                      {(payment.payment_method ?? "—").replace(/_/g, " ")}
                    </td>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      {(payment.payment_type ?? "—").replace(/_/g, " ")}
                    </td>
                    <td>
                      <StatusPill $status={payment.status ?? ""}>
                        {capitalize(payment.status ?? "")}
                      </StatusPill>
                    </td>
                    <td>{payment.transaction_reference ?? "—"}</td>
                    <td>
                      {payment.receipt_url ? (
                        <TextLink
                          href={payment.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </TextLink>
                      ) : (
                        "—"
                      )}
                    </td>
                    {renderActionsCell(payment)}
                  </tr>
                )
              )}
            </tbody>
          </PaymentsTable>
        </TableWrap>
      )}

      {activePayment && menuCoords && hasActions &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {ACTION_BUTTONS.map((btn) => {
                if (btn.key === "update" && (!canUpdate || !onUpdate || isFullyPaid(activePayment))) return null;
                if (btn.key === "delete" && (!canDelete || !onDelete)) return null;
                const Icon = btn.icon;
                return (
                  <MenuItem
                    key={btn.key}
                    type="button"
                    onClick={() => {
                      closeMenu();
                      if (btn.key === "update") onUpdate?.(activePayment.id);
                      else if (btn.key === "delete") onDelete?.(activePayment.id);
                    }}
                  >
                    <ItemIconContainer>
                      <Icon size={14} />
                    </ItemIconContainer>
                    <ItemLabel>{btn.label}</ItemLabel>
                  </MenuItem>
                );
              })}
            </DropdownMenu>
          </>,
          document.body
        )}
    </ListPanel>
  );
};

export default TeamMemberPaymentList;

const ListPanel = styled.div<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 18px;
  ${(p) =>
    p.$embedded
      ? `
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    border-radius: 0;
  `
      : `
    background: #fff;
    border-radius: 20px;
    border: 1px solid rgba(23, 23, 23, 0.06);
    box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
    padding: 24px;
  `}
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ListTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const CountBadge = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(23, 23, 23, 0.05);
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const PaymentsTable = styled.table<{ $minWidth?: number }>`
  width: 100%;
  min-width: ${(p) => p.$minWidth ?? 1100}px;
  border-collapse: collapse;

  th {
    padding: 12px 14px;
    text-align: left;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: #fafafa;
    border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  }

  th span {
    background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 80%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  td {
    padding: 12px 14px;
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: rgba(23, 23, 23, 0.82);
    border-bottom: 1px solid rgba(23, 23, 23, 0.05);
  }

  tbody tr:hover {
    background: rgba(201, 77, 170, 0.03);
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const MemberName = styled.span`
  font-weight: 700;
  color: #171717;
`;

const AmountCell = styled.span`
  font-weight: 700;
  color: #171717;
`;

const RemainingCell = styled.span`
  font-weight: 700;
  color: #e44862;
`;

const StatusPill = styled.span<{ $status: string }>`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) =>
    p.$status === "completed"
      ? "rgba(49,166,106,0.12)"
      : p.$status === "pending"
        ? "rgba(240,154,62,0.15)"
        : "rgba(228,72,98,0.12)"};
  color: ${(p) =>
    p.$status === "completed"
      ? "#31a66a"
      : p.$status === "pending"
        ? "#f09a3e"
        : "#e44862"};
`;

const TextLink = styled.a`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #c94daa;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
